import { useCallback, useEffect, useState, type ReactNode } from "react";

import { Badge, Button, Skeleton } from "../../../shared/ui";
import { ErrorRetry } from "../ErrorRetry";
import { OperatorDenied, OperatorRefused } from "../operatorHttp";
import { PermissionDenied } from "../PermissionDenied";
import { isSessionExpired, useReportSessionExpired } from "../sessionContext";
import { formatDateTime } from "../users/format";
import type { AuditApi, AuditEventDetailData } from "./auditApi";
import {
  actorLabel,
  outcomeLabel,
  outcomeTone,
  summaryValue,
} from "./auditFormat";
import { eventTypeLabel, useAuditT, type AuditKey } from "./useAuditT";

type State =
  | { kind: "loading" }
  | { kind: "ready"; event: AuditEventDetailData }
  | { kind: "notFound" }
  | { kind: "failed" }
  | { kind: "denied" };

function CopyableId({ value }: { value: string }) {
  const t = useAuditT();
  const [copied, setCopied] = useState(false);
  return (
    <span className="flex min-w-0 items-center gap-2">
      <code className="min-w-0 break-all font-mono text-footnote text-fg">
        {value}
      </code>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          void navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
          });
        }}
      >
        {copied ? t("admin.audit.copied") : t("admin.audit.copy")}
      </Button>
    </span>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 py-2">
      <dt className="text-caption text-fg-muted">{label}</dt>
      <dd className="min-w-0 text-body text-fg">{children}</dd>
    </div>
  );
}

function LinkButton({
  href,
  onOpen,
  children,
}: {
  href: string;
  onOpen: (href: string) => void;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      className="inline-flex min-h-11 items-center text-body font-medium text-accent-fg"
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        onOpen(href);
      }}
    >
      {children}
    </a>
  );
}

function summaryString(event: AuditEventDetailData, key: string) {
  const value = event.summary[key];
  return typeof value === "string" && value !== "" ? value : null;
}

/** One audit event: envelope fields and the redacted summary as text. */
export function AuditEventDetail({
  api,
  eventId,
  onBack,
  onNavigate,
}: {
  api: AuditApi;
  eventId: string;
  /** Present on narrow screens, where detail replaces the list. */
  onBack?: () => void;
  onNavigate: (href: string) => void;
}) {
  const t = useAuditT();
  const reportExpired = useReportSessionExpired();
  const [state, setState] = useState<State>({ kind: "loading" });

  const load = useCallback(
    (active: { current: boolean }) => {
      api
        .detail(eventId)
        .then((event) => {
          if (active.current) setState({ kind: "ready", event });
        })
        .catch((error: unknown) => {
          if (!active.current) return;
          if (isSessionExpired(error)) reportExpired();
          if (error instanceof OperatorDenied) {
            setState({ kind: "denied" });
          } else if (error instanceof OperatorRefused && error.status === 404) {
            setState({ kind: "notFound" });
          } else {
            setState({ kind: "failed" });
          }
        });
    },
    [api, eventId, reportExpired],
  );

  useEffect(() => {
    const active = { current: true };
    load(active);
    return () => {
      active.current = false;
    };
  }, [load]);

  const back = onBack && (
    <Button variant="ghost" size="sm" onClick={onBack}>
      {t("admin.audit.back")}
    </Button>
  );

  if (state.kind === "denied") {
    return <PermissionDenied message={t("admin.denied.audit")} />;
  }
  if (state.kind === "loading") {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        {back}
        <Skeleton height={120} />
        <span className="sr-only">{t("admin.audit.loading")}</span>
      </div>
    );
  }
  if (state.kind === "notFound") {
    return (
      <div className="flex flex-col gap-3">
        {back}
        <p className="text-body text-fg-muted">{t("admin.audit.notFound")}</p>
      </div>
    );
  }
  if (state.kind === "failed") {
    return (
      <div className="flex flex-col gap-3">
        {back}
        <ErrorRetry
          message={t("admin.audit.detailFailed")}
          onRetry={() => {
            setState({ kind: "loading" });
            load({ current: true });
          }}
        />
      </div>
    );
  }

  const { event } = state;
  const userId =
    event.resource_type === "user" && event.resource_id !== null
      ? event.resource_id
      : summaryString(event, "user_id");
  const summaryEntries = Object.entries(event.summary);
  const rows: [AuditKey, string | null][] = [
    ["admin.audit.field.reason", event.safe_reason],
  ];
  return (
    <article className="flex min-w-0 flex-col gap-3">
      {back}
      <header className="flex flex-col gap-1">
        <h2 className="text-title text-fg">
          {eventTypeLabel(t, event.event_type)}
        </h2>
        <span className="flex items-center gap-2 text-footnote text-fg-muted">
          {formatDateTime(event.occurred_at)}
          <Badge tone={outcomeTone(event.outcome)}>
            {outcomeLabel(t, event.outcome)}
          </Badge>
        </span>
      </header>
      <dl className="divide-y divide-line-subtle rounded-xl bg-raised px-4 shadow-card">
        <Field label={t("admin.audit.field.eventType")}>
          <code className="break-all font-mono text-footnote">
            {event.event_type}
          </code>
        </Field>
        <Field label={t("admin.audit.field.eventId")}>
          <CopyableId value={event.event_id} />
        </Field>
        <Field label={t("admin.audit.field.actor")}>
          <span className="flex flex-col">
            <span>{actorLabel(t, event.actor)}</span>
            <CopyableId value={event.actor.id} />
          </span>
        </Field>
        <Field label={t("admin.audit.field.resource")}>
          <span className="flex flex-col">
            <span>{event.resource_type}</span>
            {event.resource_id !== null && (
              <CopyableId value={event.resource_id} />
            )}
          </span>
        </Field>
        {rows.map(([label, value]) =>
          value === null ? null : (
            <Field key={label} label={t(label)}>
              {value}
            </Field>
          ),
        )}
        {event.correlation_id !== null && (
          <Field label={t("admin.audit.field.correlation")}>
            <CopyableId value={event.correlation_id} />
          </Field>
        )}
        {event.causation_id !== null && (
          <Field label={t("admin.audit.field.causation")}>
            <CopyableId value={event.causation_id} />
          </Field>
        )}
        {event.idempotency_ref !== null && (
          <Field label={t("admin.audit.field.idempotency")}>
            <code className="break-all font-mono text-footnote">
              {event.idempotency_ref}
            </code>
          </Field>
        )}
        <Field label={t("admin.audit.field.schemaVersion")}>
          {String(event.schema_version)}
        </Field>
      </dl>
      <section className="flex flex-col gap-1">
        <h3 className="text-footnote font-semibold text-fg-muted">
          {t("admin.audit.field.summary")}
        </h3>
        {summaryEntries.length === 0 ? (
          <p className="text-body text-fg-muted">
            {t("admin.audit.summary.empty")}
          </p>
        ) : (
          <dl className="divide-y divide-line-subtle rounded-xl bg-raised px-4 shadow-card">
            {summaryEntries.map(([key, value]) => (
              <Field key={key} label={key}>
                <span className="break-words font-mono text-footnote">
                  {summaryValue(value)}
                </span>
              </Field>
            ))}
          </dl>
        )}
      </section>
      <nav className="flex flex-col">
        {userId !== null && (
          <LinkButton
            href={`/admin/users/${encodeURIComponent(userId)}`}
            onOpen={onNavigate}
          >
            {t("admin.audit.openUser")}
          </LinkButton>
        )}
        {event.correlation_id !== null && (
          <LinkButton
            href={`/admin/logs/${encodeURIComponent(event.correlation_id)}`}
            onOpen={onNavigate}
          >
            {t("admin.audit.openLogs")}
          </LinkButton>
        )}
        {event.actor.kind === "operator" && (
          <LinkButton
            href={`/admin/access/operators/${encodeURIComponent(event.actor.id)}`}
            onOpen={onNavigate}
          >
            {t("admin.audit.openOperator")}
          </LinkButton>
        )}
      </nav>
    </article>
  );
}
